import React, { useState, useEffect } from "react";
import { FiCheckCircle, FiXCircle, FiTrash2, FiInbox } from "react-icons/fi";
import {
  getAllRequestsAdmin,
  respondToRequestAdmin,
  deleteRequestAdmin,
} from "../../../services/operations/requestAPI";

const AdminRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Response states
  const [activeRequest, setActiveRequest] = useState(null);
  const [responseAction, setResponseAction] = useState("approved");
  const [adminReason, setAdminReason] = useState("");
  
  // Delete state
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    const res = await getAllRequestsAdmin();
    if (res?.data) {
      setRequests(res.data);
    }
    setLoading(false);
  };

  const openRespondModal = (req, action) => {
    setActiveRequest(req);
    setResponseAction(action);
    setAdminReason(req.adminReason || "");
    setShowModal(true);
  };

  const handleRespond = async (e) => {
    e.preventDefault();
    const res = await respondToRequestAdmin(
      activeRequest._id,
      responseAction,
      adminReason
    );
    if (res) {
      setShowModal(false);
      fetchRequests();
    }
  };

  // Open the custom delete modal
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // Execute the actual deletion
  const confirmDelete = async () => {
    if (deleteId) {
      const res = await deleteRequestAdmin(deleteId);
      if (res) {
        fetchRequests();
      }
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Approved
          </span>
        );
      case "dismissed":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span> Dismissed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span> Pending
          </span>
        );
    }
  };

  // Helper to generate a placeholder avatar
  const getInitial = (name) => (name ? name.charAt(0).toUpperCase() : "?");

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            Manage Requests
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Review and process student profile, curriculum, and subject requests.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 shadow-sm rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    <th className="p-5 w-1/4">Student Info</th>
                    <th className="p-5 w-1/6">Request Type</th>
                    <th className="p-5 w-1/3">Request Details</th>
                    <th className="p-5 w-1/6">Status</th>
                    <th className="p-5 text-right w-1/6">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {requests.map((req) => (
                    <tr key={req._id} className="hover:bg-gray-50/80 transition-colors group">
                      {/* Student Info */}
                      <td className="p-5 align-top">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-inner">
                            {getInitial(req.student?.name)}
                          </div>
                          <div>
                            <p className="font-bold text-sm text-gray-900 leading-tight">
                              {req.student?.name || "Unknown User"}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {req.student?.email || "N/A"}
                            </p>
                            {req.student?.class && (
                              <span className="inline-block mt-1.5 px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600 border border-gray-200">
                                Current Profile: Class {req.student.class}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Request Type */}
                      <td className="p-5 align-top">
                        <span className="block text-sm font-semibold text-gray-900 capitalize mb-1.5">
                          {req.type}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                          Req Class: {req.class}
                        </span>
                      </td>

                      {/* Details */}
                      <td className="p-5 align-top max-w-sm">
                        <p className="text-sm font-bold text-gray-900 mb-1 truncate">
                          {req.title}
                        </p>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {req.description}
                        </p>
                        {req.adminReason && (
                          <div className="mt-2.5 p-2 bg-blue-50/50 rounded-lg border border-blue-100/50 text-xs text-blue-800 flex gap-2">
                            <span className="font-bold shrink-0">Reason:</span> 
                            <span className="line-clamp-2">{req.adminReason}</span>
                          </div>
                        )}
                      </td>

                      {/* Status */}
                      <td className="p-5 align-top">
                        {getStatusBadge(req.status)}
                      </td>

                      {/* Actions */}
                      <td className="p-5 align-top">
                        <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          
                          {/* Approve Button */}
                          <button
                            onClick={() => openRespondModal(req, "approved")}
                            className="p-2 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors tooltip relative"
                            title={req.status === 'approved' ? "Edit Approval Note" : "Approve Request"}
                          >
                            <FiCheckCircle size={18} />
                          </button>
                          
                          {/* Dismiss Button */}
                          <button
                            onClick={() => openRespondModal(req, "dismissed")}
                            className="p-2 text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors tooltip relative"
                            title={req.status === 'dismissed' ? "Edit Dismissal Note" : "Dismiss Request"}
                          >
                            <FiXCircle size={18} />
                          </button>
                          
                          <div className="w-px h-6 bg-gray-200 mx-1 my-auto"></div>
                          
                          {/* Delete Button */}
                          <button
                            onClick={() => handleDeleteClick(req._id)}
                            className="p-2 text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg transition-colors tooltip relative"
                            title="Delete Permanently"
                          >
                            <FiTrash2 size={18} />
                          </button>
                          
                        </div>
                      </td>
                    </tr>
                  ))}
                  {requests.length === 0 && (
                    <tr>
                      <td colSpan="5" className="p-12 text-center">
                         <div className="flex flex-col items-center justify-center text-gray-400">
                           <FiInbox size={48} className="mb-4 text-gray-300" />
                           <p className="text-lg font-medium text-gray-900">No requests in queue</p>
                           <p className="text-sm">All caught up! There are no student requests to manage.</p>
                         </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal for Admin Response */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
              <div className={`px-6 py-4 border-b ${responseAction === "approved" ? "bg-emerald-50 border-emerald-100" : "bg-rose-50 border-rose-100"}`}>
                <h2 className={`text-xl font-bold flex items-center gap-2 ${responseAction === "approved" ? "text-emerald-800" : "text-rose-800"}`}>
                  {responseAction === "approved" ? <FiCheckCircle /> : <FiXCircle />}
                  {responseAction === "approved" ? "Approve Request" : "Dismiss Request"}
                </h2>
              </div>
              
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-5 pb-5 border-b border-gray-100">
                  You are about to {responseAction} the request <strong className="text-gray-900">"{activeRequest?.title}"</strong> from <strong className="text-gray-900">{activeRequest?.student?.name}</strong>.
                </p>
                
                <form onSubmit={handleRespond}>
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Response Note (Optional)
                    </label>
                    <textarea
                      rows="3"
                      value={adminReason}
                      onChange={(e) => setAdminReason(e.target.value)}
                      placeholder={`Add a note to explain why this is being ${responseAction}...`}
                      className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-xl p-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-5 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow-sm active:scale-95 transition-all ${
                        responseAction === "approved"
                          ? "bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500/20"
                          : "bg-rose-600 hover:bg-rose-700 focus:ring-2 focus:ring-rose-500/20"
                      }`}
                    >
                      Confirm {responseAction === "approved" ? "Approval" : "Dismissal"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Custom Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col p-6 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-rose-100 mb-4">
                <FiTrash2 className="h-6 w-6 text-rose-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Request</h3>
              <p className="text-sm text-gray-500 mb-6">
                WARNING: This will permanently delete the request. This action cannot be undone. Continue?
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteId(null);
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-rose-600 rounded-xl hover:bg-rose-700 shadow-sm active:scale-95 transition-all"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRequests;